import { FC, useEffect, useMemo, useState } from 'react'
import moment from 'moment/moment'
import { useGetChangeRequestsQuery } from 'common/services/useChangeRequest'
import { RouterChildContext } from 'react-router'
import AccountStore from 'common/stores/account-store'
import { ChangeRequestSummary, User } from 'common/types/responses'
import OrganisationStore from 'common/stores/organisation-store'
import AppActions from 'common/dispatcher/app-actions'
import { Link } from 'react-router-dom'
import Utils from 'common/utils/utils'
import PageTitle from 'components/PageTitle'
import PlanBasedBanner, {
  featureDescriptions,
} from 'components/PlanBasedAccess'
import PanelSearch from 'components/PanelSearch'
import EmptyState from 'components/EmptyState'
import JSONReference from 'components/JSONReference'
import Icon from 'components/Icon'
import getUserDisplayName from 'common/utils/getUserDisplayName'

type ScheduledChangesPageType = {
  router: RouterChildContext['router']
  match: {
    params: {
      environmentId: string
      projectId: string
    }
  }
}
const ScheduledChangesPage: FC<ScheduledChangesPageType> = ({ match }) => {
  const { environmentId, projectId } = match.params
  const organisationId = AccountStore.getOrganisation()?.id
  const [_, setUpdate] = useState(Date.now())
  const [page, setPage] = useState(1)

  // @ts-ignore
  const organisation = OrganisationStore.model as any
  const users = organisation?.users as User[]

  const live_from_after = useMemo(() => {
    return new Date().toISOString()
  }, [])
  const { data: dataScheduled, isLoading } = useGetChangeRequestsQuery({
    committed: true,
    environmentId,
    live_from_after,
    page,
    page_size: 50,
  })
  useEffect(() => {
    AppActions.getOrganisation(organisationId)
  }, [organisationId])

  useEffect(() => {
    const forceUpdate = () => {
      setUpdate(Date.now())
    }
    OrganisationStore.on('change', forceUpdate)

    return () => {
      OrganisationStore.off('change', forceUpdate)
    }
  }, [])
  return (
    <div
      data-test='change-requests-page'
      id='change-requests-page'
      className='app-container container'
    >
      {!!Utils.getPlansPermission('SCHEDULE_FLAGS') && (
        <PageTitle title={featureDescriptions.SCHEDULE_FLAGS.title}>
          {featureDescriptions.SCHEDULE_FLAGS.description}
        </PageTitle>
      )}
      <PlanBasedBanner feature={'SCHEDULE_FLAGS'} theme={'page'}>
        <Flex>
          {
            <div>
              <PanelSearch
                renderSearchWithNoResults
                id='users-list'
                title=''
                className='no-pad'
                isLoading={isLoading || !dataScheduled || !organisation}
                paging={dataScheduled}
                items={dataScheduled?.results}
                renderNoResults={
                  <EmptyState
                    icon='calendar'
                    title='No scheduled changes'
                    description='To schedule a change, edit a feature flag and use the "Schedule" option when saving your changes.'
                    docsUrl={featureDescriptions.SCHEDULE_FLAGS.docs}
                  />
                }
                renderFooter={() => (
                  <JSONReference
                    className='mt-4 ml-3'
                    title={'Change Requests'}
                    json={dataScheduled?.results}
                  />
                )}
                nextPage={() => setPage(page + 1)}
                prevPage={() => setPage(page + 1)}
                goToPage={setPage}
                renderRow={({
                  created_at,
                  id,
                  live_from,
                  title,
                  user: _user,
                }: ChangeRequestSummary) => {
                  const user = users?.find((v) => v.id === _user)
                  return (
                    <Link
                      to={`/project/${projectId}/environment/${environmentId}/scheduled-changes/${id}`}
                      className='flex-row list-item clickable'
                    >
                      <Flex className='table-column px-3'>
                        <div className='font-weight-medium'>
                          <div className='d-flex align-items-center gap-2'>
                            {title}
                            <div className='d-flex align-items-center gap-1'>
                              <Icon
                                width={18}
                                name='calendar'
                                fill={'#1A2634'}
                              />
                              {moment(live_from).format('Do MMM HH:mm')}
                            </div>
                          </div>
                        </div>
                        <div className='list-item-subtitle mt-1'>
                          Created{' '}
                          {moment(created_at).format('Do MMM YYYY HH:mma')} by{' '}
                          {getUserDisplayName(user)}
                        </div>
                      </Flex>
                      <div className='table-column'>
                        <Icon name='chevron-right' fill='#9DA4AE' width={20} />
                      </div>
                    </Link>
                  )
                }}
              />
            </div>
          }
        </Flex>
      </PlanBasedBanner>
    </div>
  )
}

export default ScheduledChangesPage
